import { useMemo } from 'react'
import { useQuery } from 'react-query'

import type { Contact } from '@features/contacts/types/contact'
import { fetchContacts } from '@modules-deprecated/app/contacts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { getNormalizedImportedInvoiceTaxNumber } from '../utils/getNormalizedImportedInvoiceTaxNumber'

interface UseFetchImportedInvoiceMatchingClientOptions {
  isEnabled: boolean
  name?: string | null
  registrationNo?: string | null
  vatNo?: string | null
}

const getIsMatchingClient = (contact: Contact, vatNo?: string, registrationNo?: string): boolean =>
  (!!vatNo && getNormalizedImportedInvoiceTaxNumber(contact.vatNo) === vatNo) ||
  (!!registrationNo && getNormalizedImportedInvoiceTaxNumber(contact.registrationNo) === registrationNo)

const getNormalizedName = (name?: string | null): string => name?.trim().toLowerCase() ?? ''

const getIsSameName = (contact: Contact, name?: string): boolean =>
  !!name && getNormalizedName(contact.name) === getNormalizedName(name)

const getHasConflictingTaxNumber = (contact: Contact, vatNo?: string, registrationNo?: string): boolean =>
  (!!vatNo && !!contact.vatNo && getNormalizedImportedInvoiceTaxNumber(contact.vatNo) !== vatNo) ||
  (!!registrationNo &&
    !!contact.registrationNo &&
    getNormalizedImportedInvoiceTaxNumber(contact.registrationNo) !== registrationNo)

/**
 * Searches customers by the extracted name, because the contacts search ignores VAT numbers. An exact
 * VAT or registration number wins; otherwise a single customer with exactly the extracted name and no
 * contradicting tax number. Several such customers are reported as ambiguous. Only the first page of
 * search results is checked.
 */
export const useFetchImportedInvoiceMatchingClient = ({
  isEnabled,
  name,
  registrationNo,
  vatNo,
}: UseFetchImportedInvoiceMatchingClientOptions) => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id ?? ''
  const normalizedVatNo = getNormalizedImportedInvoiceTaxNumber(vatNo)
  const normalizedRegistrationNo = getNormalizedImportedInvoiceTaxNumber(registrationNo)
  const searchQuery = name?.trim() || undefined

  const queryParams = useMemo(
    () => ({ isCustomer: true, organizationId, q: searchQuery }),
    [organizationId, searchQuery],
  )

  const isSearchEnabled = isEnabled && !!organizationId && !!searchQuery
  const { data: contactsData, isFetchedAfterMount } = useQuery(
    [QueryKeys.Contacts, queryParams],
    () => fetchContacts(queryParams),
    { enabled: isSearchEnabled, retry: false },
  )

  const { contact, isAmbiguous } = useMemo(() => {
    if (!isSearchEnabled) {
      return { contact: undefined, isAmbiguous: false }
    }

    const contacts = contactsData?.contacts ?? []
    const taxNumberMatch = contacts.find((candidate) =>
      getIsMatchingClient(candidate, normalizedVatNo, normalizedRegistrationNo),
    )

    if (taxNumberMatch) {
      return { contact: taxNumberMatch, isAmbiguous: false }
    }

    const nameMatches = contacts.filter(
      (candidate) =>
        getIsSameName(candidate, searchQuery) &&
        !getHasConflictingTaxNumber(candidate, normalizedVatNo, normalizedRegistrationNo),
    )

    return nameMatches.length === 1
      ? { contact: nameMatches[0], isAmbiguous: false }
      : { contact: undefined, isAmbiguous: nameMatches.length > 1 }
  }, [contactsData?.contacts, isSearchEnabled, normalizedRegistrationNo, normalizedVatNo, searchQuery])

  // A cached miss is already `success`, so `isLoading` is false while this mount refetches. The
  // review form reads the client once, and has to wait until that refetch has settled.
  return { contact, isAmbiguous, isLoading: isSearchEnabled && !isFetchedAfterMount }
}
